/**
 * IDoScreen - K Part Whole I Do Practice
 *
 * Shows the 5-ball display and demonstrates how to count empty balls.
 * Reuses the ball display from the assessment component.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { useKTTS } from '../../../hooks/useKAudio';
import { TRACK_RANGES } from '../../../types/constants';

// Use PreK shared components
import {
  PracticeHeader,
  PRACTICE_PHASES,
} from '../../PreKPractice/shared';

// ============================================================================
// Constants
// ============================================================================

const K_TRACK_ID = 'TRACK_K_PARTWHOLE';

/**
 * Convert K part whole fact ID to empty count value
 * K part whole facts: FACT1679-FACT1684 map to empty counts 0-5
 */
const factIdToEmptyCount = (factId: string): number => {
  const factNumber = parseInt(factId.replace('FACT', ''));
  const [startFact] = TRACK_RANGES.TRACK_K_PARTWHOLE;
  return factNumber - startFact;
};

interface IDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'showing' | 'counting' | 'result'>('showing');
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playTTSWithCallback, isTTSPlaying } = useKTTS();

  const currentFactId = factsToPractice[currentFactIndex];
  const currentEmptyCount = currentFactId ? factIdToEmptyCount(currentFactId) : undefined;
  const filledCount = currentEmptyCount !== undefined ? 5 - currentEmptyCount : 0;

  // Reset phase when fact changes
  useEffect(() => {
    if (currentEmptyCount === undefined) return;

    // Clear any pending timeouts from previous fact
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setCurrentPhase('showing');
  }, [currentEmptyCount, currentFactIndex]);

  // Auto-advance to next fact after showing sequence
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }

    const timer = setTimeout(() => {
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // All facts completed, move to next screen
        onComplete();
      }
    }, 1000);

    setAutoAdvanceTimer(timer);
  }, [currentFactIndex, factsToPractice.length, onComplete, autoAdvanceTimer]);

  // Start the demonstration sequence when in showing phase
  useEffect(() => {
    if (currentPhase === 'showing' && currentEmptyCount !== undefined) {
      // Small delay then start counting phase
      const timeout1 = setTimeout(() => {
        setCurrentPhase('counting');

        // Brief counting phase, then show result with TTS
        const timeout2 = setTimeout(() => {
          setCurrentPhase('result');

          if (settings.speechEnabled) {
            // Play "I see X empty circles!" instruction
            const audioFile = {
              filename: `kPractice/partWhole/k_partwhole_ido_${currentEmptyCount}.mp3`,
              text: `I see ${currentEmptyCount} empty circle${currentEmptyCount === 1 ? '' : 's'}!`,
            };

            playTTSWithCallback(audioFile, () => {
              startAutoAdvance();
            }).catch(() => {
              console.error('IDoScreen part whole TTS failed for empty count:', currentEmptyCount);
              startAutoAdvance();
            });
          } else {
            startAutoAdvance();
          }
        }, 500);
        timeoutsRef.current.push(timeout2);
      }, 500);
      timeoutsRef.current.push(timeout1);
    }
  }, [currentPhase, currentEmptyCount, settings.speechEnabled, playTTSWithCallback, startAutoAdvance]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [autoAdvanceTimer]);

  // Loading state
  if (currentEmptyCount === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={
          factsToPractice.length > 0
            ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-12">
        {/* Showing Phase - Get Ready */}
        {currentPhase === 'showing' && (
          <div className="text-center">
            <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
              Get ready...
            </div>
            <div className="text-lg text-gray-500">Watch carefully!</div>
          </div>
        )}

        {/* Counting Phase - Show balls */}
        {currentPhase === 'counting' && (
          <div className="flex flex-col items-center gap-8">
            {/* 5-Ball Display */}
            <div className="flex gap-6 lg:gap-8">
              {[0, 1, 2, 3, 4].map((index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`
                    w-16 h-16 lg:w-20 lg:h-20 rounded-full shadow-lg border-4
                    ${index < filledCount
                      ? 'bg-blue-400 border-blue-500'
                      : 'bg-transparent border-dashed border-gray-400'
                    }
                  `}
                />
              ))}
            </div>

            <div className="text-xl text-gray-600 animate-pulse">
              Counting empty circles...
            </div>
          </div>
        )}

        {/* Result Phase - Show balls with answer */}
        {currentPhase === 'result' && (
          <div className="flex flex-col items-center gap-8">
            {/* 5-Ball Display */}
            <div className="flex gap-6 lg:gap-8">
              {[0, 1, 2, 3, 4].map((index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 1 }}
                  animate={{
                    scale: index >= filledCount ? [1, 1.1, 1] : 1,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index >= filledCount ? (index - filledCount) * 0.2 : 0,
                  }}
                  className={`
                    w-16 h-16 lg:w-20 lg:h-20 rounded-full shadow-lg border-4
                    ${index < filledCount
                      ? 'bg-blue-400 border-blue-500'
                      : 'bg-transparent border-dashed border-gray-400 ring-4 ring-green-400'
                    }
                  `}
                />
              ))}
            </div>

            {/* Large Answer Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="w-48 h-48 lg:w-56 lg:h-56 bg-gradient-to-br from-green-400 to-green-600 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-green-700"
              style={NUMERAL_FONT_STYLE}
            >
              <span className="text-8xl lg:text-9xl font-bold text-white">{currentEmptyCount}</span>
            </motion.div>
          </div>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IDoScreen;
